import { useMemo } from 'react';

import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';

export enum StoreClosedReason {
  NO_STORE_SELECTED = 'no_store_selected',
  POS_UNAVAILABLE = 'pos_unavailable',
  MOBILE_ORDERING_DISABLED = 'mobile_ordering_disabled',
  DELIVERY_HOURS_CLOSED = 'delivery_hours_closed',
  PICKUP_HOURS_CLOSED = 'pickup_hours_closed',
  DELIVERY_FEATURE_FLAG_DISABLED = 'delivery_feature_flag_disabled',
  PICKUP_FEATURE_FLAG_DISABLED = 'pickup_feature_flag_disabled',
  SERVICE_MODE_UNAVAILABLE = 'service_mode_unavailable',
}

export const useStoreClosedReason = (activeStore: IRestaurant) => {
  const { isSelectingStore } = useStoreContext();
  const { serviceMode } = useServiceModeContext();

  const {
    isStoreAvailable,
    isStoreAvailableForPickup,
    isOpenForDelivery,
    isOpenForPickup,
    isPOSAvailable,
    isMobileOrderingAvailable,
    getStoreServiceModeStatuses,
  } = useStoreAvailabilityFns();

  const storeStatus = useMemo(
    () => ({
      isPosAvailable: isPOSAvailable(activeStore),
      isMobileOrderingSupported: isMobileOrderingAvailable(activeStore),
      isDeliveryOpen: isOpenForDelivery(activeStore),
      isPickupOpen: isOpenForPickup(activeStore),
    }),
    [isPOSAvailable, activeStore, isMobileOrderingAvailable, isOpenForDelivery, isOpenForPickup]
  );

  const closedReason = useMemo((): StoreClosedReason | null => {
    if (isSelectingStore) {
      return null;
    }

    if (!activeStore?.number) {
      return StoreClosedReason.NO_STORE_SELECTED;
    }

    if (!storeStatus.isPosAvailable) {
      return StoreClosedReason.POS_UNAVAILABLE;
    }

    if (!storeStatus.isMobileOrderingSupported) {
      return StoreClosedReason.MOBILE_ORDERING_DISABLED;
    }

    // Check service mode specific availability
    if (serviceMode === ServiceMode.DELIVERY) {
      if (!storeStatus.isDeliveryOpen) {
        return StoreClosedReason.DELIVERY_HOURS_CLOSED;
      }

      // Delivery feature flag disabled
      const serviceModeStatuses = getStoreServiceModeStatuses(activeStore);
      const deliveryStatus = serviceModeStatuses.find(
        status => status.serviceMode === ServiceMode.DELIVERY
      );
      if (deliveryStatus?.featureFlagDisabled) {
        return StoreClosedReason.DELIVERY_FEATURE_FLAG_DISABLED;
      }

      if (!isStoreAvailable(activeStore, serviceMode)) {
        return StoreClosedReason.SERVICE_MODE_UNAVAILABLE;
      }
    } else {
      if (!storeStatus.isPickupOpen) {
        return StoreClosedReason.PICKUP_HOURS_CLOSED;
      }

      // Check if all pickup modes are feature flag disabled
      const serviceModeStatuses = getStoreServiceModeStatuses(activeStore);
      const pickupStatuses = serviceModeStatuses.filter(
        status => status.serviceMode !== ServiceMode.DELIVERY
      );
      const allPickupDisabled = pickupStatuses.every(status => status.featureFlagDisabled);

      if (allPickupDisabled) {
        return StoreClosedReason.PICKUP_FEATURE_FLAG_DISABLED;
      }

      // Check specific service mode availability
      if (serviceMode && !isStoreAvailable(activeStore, serviceMode)) {
        return StoreClosedReason.SERVICE_MODE_UNAVAILABLE;
      }

      if (!isStoreAvailableForPickup(activeStore)) {
        return StoreClosedReason.SERVICE_MODE_UNAVAILABLE;
      }
    }

    // Store is available - return null (no closed reason)
    return null;
  }, [
    activeStore,
    isSelectingStore,
    serviceMode,
    storeStatus,
    isStoreAvailable,
    isStoreAvailableForPickup,
    getStoreServiceModeStatuses,
  ]);

  const closedReasonDetails = useMemo(() => {
    return {
      closedReason,
      storeNumber: activeStore?.number || null,
      storeName: activeStore?.name || null,
      serviceMode,
      hasMobileOrdering: activeStore?.hasMobileOrdering || false,
      ...storeStatus,
    };
  }, [closedReason, activeStore, serviceMode, storeStatus]);

  return {
    closedReason,
    closedReasonDetails,
  };
};
